(() => {
  const drawerSelector = '[data-smaxup-cart-drawer]';
  const rootPath = () => window.Shopify?.routes?.root || '/';

  const updateHeaderCount = (count) => {
    document.querySelectorAll('[data-smaxup-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-label', `Cart, ${count} items`);
    });
    document.querySelectorAll('[data-smaxup-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  const replaceDrawer = (html, trigger) => {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = parsed.querySelector(drawerSelector);
    const currentDrawer = document.querySelector(drawerSelector);
    if (!nextDrawer || !currentDrawer) return null;
    currentDrawer.replaceWith(nextDrawer);
    const count = Number(nextDrawer.dataset.cartCount || 0);
    updateHeaderCount(count);
    nextDrawer.open(trigger);
    return nextDrawer;
  };

  const fetchDrawerSection = async (sectionId) => {
    const response = await fetch(`${rootPath()}cart?section_id=${encodeURIComponent(sectionId)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart drawer could not be refreshed.');
    return response.text();
  };

  class SmaxupCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.smaxup-cart-drawer__panel');
      this.querySelectorAll('[data-smaxup-cart-close]').forEach((control) => {
        control.addEventListener('click', (event) => {
          if (control.tagName === 'A') return;
          event.preventDefault();
          this.close();
        });
      });
      this.querySelectorAll('[data-smaxup-cart-change]').forEach((control) => {
        control.addEventListener('click', () => this.changeLine(control));
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('smaxup-cart-drawer-open');
      this.panel?.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('smaxup-cart-drawer-open');
      this.trigger?.focus?.();
    }

    async changeLine(control) {
      const line = Number(control.dataset.line);
      const quantity = Math.max(0, Number(control.dataset.quantity));
      if (!line || Number.isNaN(quantity)) return;
      control.disabled = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${rootPath()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        if (!response.ok) throw new Error('Cart quantity could not be updated.');
        const cart = await response.json();
        const html = await fetchDrawerSection(this.dataset.sectionId);
        replaceDrawer(html, this.trigger);
        updateHeaderCount(cart.item_count || 0);
      } catch (error) {
        const status = this.querySelector('[data-smaxup-cart-status]');
        if (status) status.textContent = error.message;
        control.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('smaxup-cart-drawer')) customElements.define('smaxup-cart-drawer', SmaxupCartDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-smaxup-cart-trigger]');
    if (!trigger) return;
    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = new URL(form.action, window.location.origin);
    if (!/\/cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;

    event.preventDefault();
    const submitter = event.submitter || form.querySelector('[name="add"]');
    submitter?.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;

    try {
      const formData = new FormData(form);
      formData.set('sections', drawer.dataset.sectionId);
      formData.set('sections_url', window.location.pathname);
      const response = await fetch(action.toString(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'This item could not be added to the cart.');
      let html = result.sections?.[drawer.dataset.sectionId];
      if (!html) html = await fetchDrawerSection(drawer.dataset.sectionId);
      const nextDrawer = replaceDrawer(html, submitter);
      if (!nextDrawer) throw new Error('The item was added, but the cart drawer could not be opened.');
    } catch (error) {
      const currentDrawer = document.querySelector(drawerSelector);
      const status = currentDrawer?.querySelector('[data-smaxup-cart-status]');
      if (status) status.textContent = error.message;
      currentDrawer?.open(submitter);
    } finally {
      submitter?.removeAttribute('aria-busy');
      if (submitter) submitter.disabled = false;
    }
  }, true);
})();
